// Shared beacon event shape. Leaf module so beacon-edge.ts (handler) and
// beacon-bigquery.ts (sink) both depend on it WITHOUT a circular import
// (dep-cruiser no-circular). Extracted from beacon-edge.ts 2026-06-10.
export interface BeaconEvent {
  name: string;
  // Stable identity for one logical emission. The edge preserves this across
  // queue retries and owns the params.event_id projection written to BigQuery.
  event_id?: string;
  // First-party browser identity. This is the stable owned ID written to
  // events_raw and must never switch to Google's cookie identity mid-session.
  client_id?: string;
  // Google Measurement Protocol transport identity. These are resolved from
  // the live _ga cookies at flush time, separately from the owned client_id.
  // GA delivery requires both validated fields and never falls back to
  // client_id. The BigQuery sink copies a valid measurement client into params
  // for explicit cross-source joins while preserving owned client_id.
  measurement_client_id?: string;
  measurement_session_id?: string;
  forward_to_ga4?: boolean;
  // Legacy deploy-skew compatibility only. The Jul-14 GA-only page-view mirror
  // was retired because MP-only visits lack GA's automatic first_visit and
  // session_start lifecycle. Both sink paths must still discard queued mirrors.
  ga4_forward_only?: boolean;
  page_location?: string;
  page_title?: string;
  page_referrer?: string;
  engagement_time_msec?: number;
  params?: Record<string, unknown>;
}

const EVENT_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:@-]{7,79}$/;

export function validatedEventId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return EVENT_ID_RE.test(trimmed) ? trimmed : null;
}

export function createEventId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export function eventIdFromProperties(properties?: { event_id?: unknown }): string {
  return validatedEventId(properties?.event_id) ?? createEventId();
}

// GA's browser cookie client ID is two decimal components. Bounding each
// component at 20 digits accepts the current 10-digit shape with ample
// headroom while rejecting owned UUIDs and attacker-controlled junk.
export function validatedMeasurementClientId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return /^\d{1,20}\.\d{1,20}$/.test(trimmed) ? trimmed : null;
}

export function validatedMeasurementSessionId(value: unknown): string | null {
  return typeof value === "string" && /^\d{6,20}$/.test(value) ? value : null;
}
